import { getAddQuantityAvailability } from './cart-quantity-availability.js';
import {
  buildBundleQuantityPlan,
  executeBundleQuantityPlan,
} from './cart-bundle-quantity.js';
import {
  isCartMutationPending,
  isCartRecoveryRequired,
  lockCartMutationRecovery,
  runRecoverableCartMutation,
  subscribeCartMutationState,
} from './cart-mutation-coordinator.js';
import {
  CartRecoveryRequiredError,
  awaitCartMutationResponse,
  isCartMutationUncertain,
  readCartMutationJson,
  recoverAuthoritativeCart,
  requireCartSectionHtml,
  validateAuthoritativeCartSnapshot,
} from './cart-response-recovery.js';
import { initializeCartRecommendations } from './cart-recommendations.js';
import { syncTierShipping } from './cart-tier-shipping.js?v=20261012-owner-display';
import { createNumberTicker } from './number-ticker.js';
import { buildBundleCartAddRequest } from './product-bundle-offer.js?v=20261012-gold-cover-preorder';
import {
  hasValidPrivateMysteryAddReceipt,
  initializePrivateMysteryCheckoutGuard,
  preparePrivateMysteryAdd,
  runPrivateMysteryAddMutation,
  runPrivateMysteryLineMutation,
} from './private-mystery-hold.js';

// Keep response classification local so a partial theme asset upload cannot
// prevent the entire bag drawer module from loading.
const resolveAddToBagStockError = ({ status, description, message } = {}) => {
  const sourceMessage =
    typeof description === 'string'
      ? description
      : typeof message === 'string'
        ? message
        : '';
  const soldOut = Number(status) === 422 && /\bsold out\b/i.test(sourceMessage);

  return {
    soldOut,
    message: soldOut
      ? 'This item just sold out and wasn’t added to your bag.'
      : sourceMessage,
  };
};

/*
 * BAG DRAWER MOTION STORYBOARD
 *
 *    0ms  open starts from the current visual position or 36px to the right
 *  260ms  drawer settles at full opacity
 *
 *    0ms  close starts from the current visual position
 *  200ms  drawer softens and moves 28px right, then returns focus
 */
const DRAWER_MOTION = {
  enterDuration: 260,
  exitDuration: 200,
  enterOffset: 'clamp(24px, 6vw, 64px)',
  exitOffset: 'clamp(20px, 5vw, 48px)',
  enterEasing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  exitEasing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
};
const SHIPPING_FILL_DURATION = 180;
const SHIPPING_CELEBRATION_GAIN = 0.25;
const SHIPPING_TEXT_SWAP_DURATION = 150;
const CART_PREFLIGHT_TIMEOUT = 12_000;

const drawer = document.querySelector('[data-cart-drawer]');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const cartSectionId = 'cart-drawer';
const routesRoot = window.Shopify?.routes?.root || '/';

initializePrivateMysteryCheckoutGuard();

const getCartPageSectionId = () => document
  .querySelector('[data-route-entry-root="cart"]')
  ?.closest('[id^="shopify-section-"]')
  ?.id.replace('shopify-section-', '') || '';

const getRequestedCartSections = () => [cartSectionId, getCartPageSectionId()].filter(Boolean);

if (drawer) {
  const cartQuantityTickers = new WeakMap();
  const quantityInputTimers = new WeakMap();
  const retryActions = new WeakMap();
  const nativeFormTrackingSuppressedEvents = new WeakSet();
  let activeTrigger = null;
  let closeTimer = 0;
  let drawerAnimation = null;
  let confettiTimer = 0;
  let confettiCleanupTimer = 0;
  let pendingShippingCelebration = null;

  const syncCheckoutPending = (pending = isCartMutationPending()) => {
    drawer.querySelectorAll('[name="checkout"]').forEach((control) => {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;

      if (pending) {
        if (!control.disabled) control.dataset.cartMutationDisabled = 'true';
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        return;
      }

      if (control.dataset.cartMutationDisabled === 'true') {
        control.disabled = false;
        delete control.dataset.cartMutationDisabled;
        control.removeAttribute('aria-disabled');
      }
    });

    if (!pending && !isCartRecoveryRequired()) {
      document.querySelectorAll('[data-product-form-error][data-cart-recovery-error]').forEach((formError) => {
        window.SigmaErrorFeedback?.clear({
          wrapper: formError.closest('form'),
          message: formError,
        });
        formError.textContent = '';
        formError.hidden = true;
        delete formError.dataset.cartRecoveryError;
      });
    }
  };

  subscribeCartMutationState(syncCheckoutPending);
  let celebrationAudioContext = null;
  let celebrationAudioBufferPromise = null;
  let celebrationAudioSource = null;
  let pageScrollX = 0;

  const readCartQuantities = (container = drawer) => new Map(
    [...container.querySelectorAll('[data-cart-line-item]')].map((lineItem) => {
      const input = lineItem.querySelector('[data-cart-quantity-input]');
      return [lineItem.dataset.cartKey || '', Number(input?.value) || 1];
    }),
  );

  const initializeCartQuantityTickers = (previousQuantities = new Map()) => {
    drawer.querySelectorAll('[data-cart-line-item]').forEach((lineItem) => {
      const input = lineItem.querySelector('[data-cart-quantity-input]');
      const visual = lineItem.querySelector('[data-cart-quantity-visual]');
      const picker = input?.closest('.cart-line__quantity');

      if (!(input instanceof HTMLInputElement) || !(visual instanceof HTMLElement) || !picker) {
        return;
      }

      const quantity = Number(input.value) || 1;
      const previousQuantity = previousQuantities.get(lineItem.dataset.cartKey || '');
      const ticker = createNumberTicker(visual, {
        initialValue: String(previousQuantity ?? quantity),
        reducedMotion,
      });

      cartQuantityTickers.set(visual, ticker);
      picker.classList.add('is-animated');
      ticker.set(String(quantity), {
        animate: previousQuantity !== undefined && drawer.open,
        direction: quantity >= previousQuantity ? 1 : -1,
        numericValue: quantity,
      });
    });
  };
  let pageScrollY = 0;

  const getTriggers = () => document.querySelectorAll('[data-cart-drawer-trigger]');
  const getDrawerContent = () => drawer.querySelector('[data-cart-drawer-content]');
  const getStatus = () => drawer.querySelector('[data-cart-status]');
  const getGlobalError = () => drawer.querySelector('[data-cart-global-error]');

  const initializeBundleQuantityControls = (root = drawer) => {
    root.querySelectorAll('[data-cart-bundle-parent-control]').forEach((control) => {
      const lineItem = control.closest('[data-cart-line-item]');
      const quantity = Number(lineItem?.dataset.cartQuantity) || 1;

      if (control instanceof HTMLInputElement) {
        control.readOnly = false;
        control.removeAttribute('aria-readonly');
        return;
      }

      if (control instanceof HTMLButtonElement) {
        control.disabled = control.dataset.cartAction === 'decrease' && quantity <= 1;
      }
    });
  };

  const getBundleQuantityPlan = (parentLine, nextParentQuantity, root = drawer) => {
    if (
      !(parentLine instanceof HTMLElement)
      || parentLine.dataset.cartBundleRole !== 'parent'
      || !parentLine.hasAttribute('data-cart-has-per-parent-bundle-child')
    ) {
      return null;
    }

    const parentKey = parentLine.dataset.cartKey || '';
    const bundleInstance = parentLine.dataset.cartBundleInstance || '';
    const children = [...root.querySelectorAll('[data-cart-line-item][data-cart-parent-key]')]
      .filter((lineItem) => (
        lineItem.dataset.cartParentKey === parentKey
        && lineItem.dataset.cartBundleRole === 'add_on'
        && lineItem.dataset.cartBundleInstance === bundleInstance
      ))
      .map((lineItem) => ({
        key: lineItem.dataset.cartKey,
        variantId: lineItem.dataset.cartVariantId,
        bundleInstance: lineItem.dataset.cartBundleInstance,
        currentQuantity: Number(lineItem.dataset.cartQuantity),
        componentQuantity: Number(lineItem.dataset.cartComponentQuantity),
        quantityMode: lineItem.dataset.cartQuantityMode,
        bundleGroup: lineItem.dataset.cartBundleGroup,
      }));

    return buildBundleQuantityPlan({
      parentKey,
      parentVariantId: parentLine.dataset.cartVariantId,
      bundleInstance,
      parentQuantity: Number(parentLine.dataset.cartQuantity),
      nextParentQuantity,
      children,
    });
  };

  const resetNoteDraft = () => {
    const note = drawer.querySelector('[data-cart-note]');

    if (note) {
      note.value = note.defaultValue;
      note.setAttribute('aria-invalid', 'false');
    }

    hideFeedback(drawer.querySelector('#CartNoteError'));
  };

  const setClearConfirmation = (confirming, { restoreFocus = false } = {}) => {
    const toolbar = drawer.querySelector('[data-cart-clear-toolbar]');
    const clearButton = drawer.querySelector('[data-cart-clear]');
    const cancelButton = drawer.querySelector('[data-cart-clear-cancel]');

    if (!toolbar || !clearButton || !cancelButton) {
      return;
    }

    toolbar.toggleAttribute('data-cart-clear-confirming', confirming);
    clearButton.toggleAttribute('data-cart-clear-confirmed', confirming);
    clearButton.setAttribute('aria-label', confirming ? 'Confirm empty bag' : 'Empty bag');
    cancelButton.disabled = !confirming;
    cancelButton.tabIndex = confirming ? 0 : -1;
    cancelButton.setAttribute('aria-hidden', String(!confirming));

    if (restoreFocus) {
      clearButton.focus({ preventScroll: true });
    }
  };

  const getCelebrationAudioContext = () => {
    if (celebrationAudioContext) {
      return celebrationAudioContext;
    }

    const AudioContextConstructor = window.AudioContext ?? window.webkitAudioContext;

    if (!AudioContextConstructor || !drawer.dataset.cartCelebrationSrc) {
      return null;
    }

    try {
      celebrationAudioContext = new AudioContextConstructor();
      return celebrationAudioContext;
    } catch (error) {
      return null;
    }
  };

  const loadCelebrationAudio = (context) => {
    if (!celebrationAudioBufferPromise) {
      celebrationAudioBufferPromise = fetch(drawer.dataset.cartCelebrationSrc)
        .then((response) => {
          if (!response.ok) {
            throw new Error('The celebration sound could not be loaded.');
          }

          return response.arrayBuffer();
        })
        .then((audioData) => context.decodeAudioData(audioData))
        .catch((error) => {
          celebrationAudioBufferPromise = null;
          throw error;
        });
    }

    return celebrationAudioBufferPromise;
  };

  const prepareShippingCelebrationAudio = () => {
    const context = getCelebrationAudioContext();

    if (!context) {
      return;
    }

    if (context.state === 'suspended') {
      void context.resume().catch(() => {});
    }

    void loadCelebrationAudio(context).catch(() => {});
  };

  document.addEventListener('pointerdown', prepareShippingCelebrationAudio, { capture: true, once: true });
  document.addEventListener('keydown', prepareShippingCelebrationAudio, { capture: true, once: true });

  const playShippingCelebrationAudio = async () => {
    const context = getCelebrationAudioContext();

    if (!context) {
      return;
    }

    try {
      if (context.state === 'suspended') {
        await context.resume();
      }

      if (context.state !== 'running') {
        return;
      }

      const buffer = await loadCelebrationAudio(context);
      const source = context.createBufferSource();
      const gain = context.createGain();

      try {
        celebrationAudioSource?.stop();
      } catch (error) {}

      source.buffer = buffer;
      gain.gain.value = SHIPPING_CELEBRATION_GAIN;
      source.connect(gain).connect(context.destination);
      celebrationAudioSource = source;
      source.onended = () => {
        if (celebrationAudioSource === source) {
          celebrationAudioSource = null;
        }

        source.disconnect();
        gain.disconnect();
      };
      source.start();
    } catch (error) {}
  };

  const runShippingConfetti = () => {
    window.clearTimeout(confettiTimer);
    window.clearTimeout(confettiCleanupTimer);
    drawer.querySelector('[data-cart-confetti]')?.remove();

    if (reducedMotion.matches) {
      return;
    }

    const shipping = drawer.querySelector('[data-cart-shipping]');

    if (!shipping || shipping.dataset.cartShippingState !== 'unlocked') {
      return;
    }

    void playShippingCelebrationAudio();

    const confetti = document.createElement('span');
    const particles = document.createDocumentFragment();
    confetti.className = 'cart-empty__confetti';
    confetti.dataset.cartConfetti = '';
    confetti.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < 56; index += 1) {
      const particle = document.createElement('span');
      const x = 2 + Math.random() * 96;
      const drift = (Math.random() - 0.5) * 190;
      const launch = -28 - Math.random() * 76;
      const fall = 170 + Math.random() * 170;
      const rotation = (Math.random() > 0.5 ? 1 : -1) * (480 + Math.random() * 760);
      const width = 4 + Math.random() * 4;
      const height = 7 + Math.random() * 7;

      particle.className = `cart-empty__confetti-particle cart-empty__confetti-particle--${(index % 4) + 1}`;
      particle.style.setProperty('--cart-confetti-x', `${x}%`);
      particle.style.setProperty('--cart-confetti-drift', `${drift}px`);
      particle.style.setProperty('--cart-confetti-launch-x', `${drift * 0.42}px`);
      particle.style.setProperty('--cart-confetti-mid-x', `${drift * 0.72}px`);
      particle.style.setProperty('--cart-confetti-late-x', `${drift * 0.92}px`);
      particle.style.setProperty('--cart-confetti-fall', `${fall}px`);
      particle.style.setProperty('--cart-confetti-launch-y', `${launch * 0.8 + fall * 0.03}px`);
      particle.style.setProperty('--cart-confetti-mid-y', `${launch * 0.6 + fall * 0.18}px`);
      particle.style.setProperty('--cart-confetti-late-y', `${launch * 0.25 + fall * 0.5}px`);
      particle.style.setProperty('--cart-confetti-rotation', `${rotation}deg`);
      particle.style.setProperty('--cart-confetti-rotation-launch', `${rotation * 0.34}deg`);
      particle.style.setProperty('--cart-confetti-rotation-mid', `${rotation * 0.62}deg`);
      particle.style.setProperty('--cart-confetti-rotation-late', `${rotation * 0.84}deg`);
      particle.style.setProperty('--cart-confetti-width', `${width}px`);
      particle.style.setProperty('--cart-confetti-height', `${height}px`);
      particle.style.setProperty('--cart-confetti-delay', `${Math.random() * 100}ms`);
      particle.style.setProperty('--cart-confetti-duration', `${940 + Math.random() * 420}ms`);
      particles.append(particle);
    }

    confetti.append(particles);
    shipping.append(confetti);

    confettiCleanupTimer = window.setTimeout(() => confetti.remove(), 1700);
  };

  const playShippingCelebration = () => {
    if (!pendingShippingCelebration) {
      return;
    }

    const shippingFill = drawer.querySelector('[data-cart-shipping-fill]');
    const { from, to } = pendingShippingCelebration;
    pendingShippingCelebration = null;

    if (!shippingFill) {
      return;
    }

    shippingFill.style.setProperty('--cart-shipping-progress', from);

    if (reducedMotion.matches) {
      shippingFill.style.setProperty('--cart-shipping-progress', to);
      return;
    }

    shippingFill.getBoundingClientRect();
    window.requestAnimationFrame(() => {
      shippingFill.style.setProperty('--cart-shipping-progress', to);
      confettiTimer = window.setTimeout(runShippingConfetti, SHIPPING_FILL_DURATION + 40);
    });
  };

  const setExpanded = (expanded) => {
    getTriggers().forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
  };

  const syncHeaderCount = () => {
    const drawerContent = getDrawerContent();
    const itemCount = Number(drawerContent?.dataset.cartItemCount || 0);
    const cartTotal = drawerContent?.dataset.cartTotal;

    getTriggers().forEach((trigger) => {
      let count = trigger.querySelector('.site-header__cart-count');
      const total = trigger.querySelector('[data-cart-total]');
      const supportsCount = trigger.matches('.site-header__icon-button, .site-header__desktop-cart');

      if (supportsCount && itemCount > 0 && !count) {
        count = document.createElement('span');
        count.className = 'site-header__cart-count';
        count.setAttribute('aria-hidden', 'true');
        const countHost = trigger.querySelector('.site-header__utility-icon') || trigger;
        countHost?.append(count);
      }

      if (count) {
        count.textContent = String(Math.min(itemCount, 99));
        count.dataset.countDigits = itemCount > 9 ? '2' : '1';
        count.hidden = itemCount === 0;
      }

      const itemLabel = itemCount === 1 ? 'item' : 'items';
      trigger.setAttribute('aria-label', itemCount > 0 ? `Bag, ${itemCount} ${itemLabel}` : 'Bag');
      if (total && cartTotal) total.textContent = cartTotal;
    });
  };

  const setButtonLoading = (button, loading) => {
    if (!(button instanceof HTMLElement) || !button.matches('.button, [data-button-loading-control]')) {
      return;
    }

    button.dispatchEvent(
      new CustomEvent('sigma:button-loading', {
        bubbles: true,
        detail: { loading },
      }),
    );
  };

  const setGoldCoverAdded = (button, added, target = {}) => {
    if (!(button instanceof HTMLButtonElement) || !button.matches('[data-gold-cover-add]')) return;
    button.dataset.goldCoverActionLabel ||= button.getAttribute('aria-label') || '';
    button.toggleAttribute('data-gold-cover-added', added);
    button.setAttribute('aria-label', added ? 'Remove Gold Cover from bag.' : button.dataset.goldCoverActionLabel);
    button.dataset.buttonLoadingText = added ? 'Removing...' : 'Adding...';

    if (added) {
      button.disabled = false;
      button.dataset.goldCoverCartKey = String(target.key || button.dataset.goldCoverCartKey || '');
      button.dataset.goldCoverBundleInstance = String(
        target.bundleInstance || button.dataset.goldCoverBundleInstance || '',
      );
      return;
    }

    button.removeAttribute('data-gold-cover-confirming');
    delete button.dataset.goldCoverCartKey;
    delete button.dataset.goldCoverBundleInstance;
  };

  const isGoldCoverItem = (item, button, { requireStoredInstance = false } = {}) => {
    const goldCover = button.closest('[data-gold-cover][data-gold-cover-variant-id]');
    const properties = item?.properties || {};
    const storedInstance = button.dataset.goldCoverBundleInstance || '';
    const variantId = String(item?.variant_id ?? item?.id ?? '');

    return Boolean(
      goldCover
      && variantId === String(goldCover.dataset.goldCoverVariantId || '')
      && properties._sigma_bundle_role === 'add_on'
      && properties._sigma_bundle_group === 'gold-cover'
      && (!requireStoredInstance || (
        storedInstance
        && properties._sigma_bundle_instance === storedInstance
      )),
    );
  };

  const getGoldCoverTargetFromResponse = (button, responseData) => {
    const item = responseData?.items?.find((candidate) => isGoldCoverItem(candidate, button));

    if (!item?.key || !item.properties?._sigma_bundle_instance) return null;

    return {
      key: String(item.key),
      bundleInstance: String(item.properties._sigma_bundle_instance),
    };
  };

  const resolveGoldCoverCartItem = (button, cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const storedKey = button.dataset.goldCoverCartKey || '';
    const exactKeyItem = items.find((item) => (
      storedKey
      && String(item.key || '') === storedKey
      && isGoldCoverItem(item, button, { requireStoredInstance: true })
    ));

    return exactKeyItem || items.find((item) => (
      isGoldCoverItem(item, button, { requireStoredInstance: true })
    ));
  };

  const getCartItemParentKey = (item) => String(
    item?.parent_relationship?.parent_key
    || item?.parent_relationship?.parent?.key
    || '',
  );

  const getSelectedLineProperties = (formData) => {
    const properties = {};

    for (const [name, value] of formData.entries()) {
      const match = /^properties\[(.+)]$/.exec(name);
      if (!match || match[1].startsWith('_') || typeof value !== 'string' || !value) continue;
      properties[match[1]] = value;
    }

    return properties;
  };

  const getPublicLineProperties = (properties) => Object.fromEntries(
    Object.entries(properties || {})
      .filter(([name, value]) => !name.startsWith('_') && value != null && value !== '')
      .map(([name, value]) => [name, String(value)]),
  );

  const haveSameLineProperties = (selected, existing) => {
    const selectedEntries = Object.entries(selected).sort(([left], [right]) => left.localeCompare(right));
    const existingEntries = Object.entries(existing).sort(([left], [right]) => left.localeCompare(right));

    return selectedEntries.length === existingEntries.length
      && selectedEntries.every(([name, value], index) => (
        existingEntries[index]?.[0] === name && existingEntries[index]?.[1] === value
      ));
  };

  const getSelectedSellingPlanId = (formData) => String(formData.get('selling_plan') || '');

  const getCartItemSellingPlanId = (item) => String(
    item?.selling_plan_allocation?.selling_plan?.id
    || item?.selling_plan_allocation?.selling_plan_id
    || '',
  );

  const getGoldCoverCartState = (button, cart, formData) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const selectedVariantId = String(formData.get('id') || '');
    const selectedSellingPlanId = getSelectedSellingPlanId(formData);
    const selectedProperties = getSelectedLineProperties(formData);
    const parents = items.filter((item) => (
      !getCartItemParentKey(item)
      && !isGoldCoverItem(item, button)
      && String(item.variant_id ?? item.id ?? '') === selectedVariantId
      && getCartItemSellingPlanId(item) === selectedSellingPlanId
      && haveSameLineProperties(
        selectedProperties,
        getPublicLineProperties(item.properties),
      )
    ));

    for (const parent of parents) {
      const cover = items.find((item) => (
        getCartItemParentKey(item) === String(parent.key || '')
        && isGoldCoverItem(item, button)
      ));

      if (cover) return { parent, cover, standaloneCover: null };
    }

    const goldCover = button.closest('[data-gold-cover][data-gold-cover-variant-id]');
    const coverVariantId = String(goldCover?.dataset.goldCoverVariantId || '');
    const coverSellingPlanId = String(goldCover?.dataset.goldCoverSellingPlanId || '');
    const standaloneCover = items.find((item) => (
      !getCartItemParentKey(item)
      && String(item.variant_id ?? item.id ?? '') === coverVariantId
      && (!coverSellingPlanId || getCartItemSellingPlanId(item) === coverSellingPlanId)
    ));
    const uncoveredParent = parents.find((item) => !item.properties?._sigma_bundle_role) || parents[0];

    return {
      parent: uncoveredParent || null,
      cover: null,
      standaloneCover: standaloneCover || null,
    };
  };

  const buildGoldCoverExistingParentRequest = (bundleRequest, parent, button) => {
    const coverVariantId = String(
      button.closest('[data-gold-cover][data-gold-cover-variant-id]')?.dataset.goldCoverVariantId || '',
    );
    const cover = bundleRequest?.payload?.items?.find((item) => (
      String(item?.id || '') === coverVariantId
      && item?.properties?._sigma_bundle_role === 'add_on'
      && item?.properties?._sigma_bundle_group === 'gold-cover'
    ));

    if (!cover || !parent?.key || !Number.isInteger(Number(parent.quantity)) || Number(parent.quantity) < 1) {
      throw new Error('Gold Cover could not be attached to the matching bag item. Please try again.');
    }

    const parentProperties = parent.properties || {};
    const properties = { ...cover.properties };

    if (parentProperties._sigma_bundle_instance) {
      properties._sigma_bundle_instance = parentProperties._sigma_bundle_instance;
      if (parentProperties._sigma_offer_id) properties._sigma_offer_id = parentProperties._sigma_offer_id;
      if (parentProperties._sigma_offer_version) {
        properties._sigma_offer_version = parentProperties._sigma_offer_version;
      }
    }

    const attachedCover = {
      ...cover,
      quantity: Number(parent.quantity),
      properties,
      parent_line_key: String(parent.key),
    };
    delete attachedCover.parent_id;

    return {
      ...bundleRequest,
      childCount: 1,
      payload: {
        ...bundleRequest.payload,
        items: [attachedCover],
      },
    };
  };

  const syncGoldCoverButtonsFromDrawer = () => {
    document.querySelectorAll('[data-gold-cover-add][data-gold-cover-added]').forEach((button) => {
      if (button.form?.hasAttribute('aria-busy')) return;

      const goldCover = button.closest('[data-gold-cover][data-gold-cover-variant-id]');
      const storedKey = button.dataset.goldCoverCartKey || '';
      const storedInstance = button.dataset.goldCoverBundleInstance || '';
      const variantId = String(goldCover?.dataset.goldCoverVariantId || '');
      const line = [...drawer.querySelectorAll('[data-cart-line-item]')].find((candidate) => (
        candidate.dataset.cartBundleRole === 'add_on'
        && candidate.dataset.cartVariantId === variantId
        && (
          (storedKey && candidate.dataset.cartKey === storedKey)
          || (storedInstance && candidate.dataset.cartBundleInstance === storedInstance)
        )
      ));

      if (line) {
        setGoldCoverAdded(button, true, {
          key: line.dataset.cartKey,
          bundleInstance: line.dataset.cartBundleInstance,
        });
      } else {
        setGoldCoverAdded(button, false);
      }
    });
  };

  const restoreSubmitterDisabledState = (submitter) => {
    if (!(submitter instanceof HTMLButtonElement) || submitter.matches('.button')) {
      return;
    }

    const goldCover = submitter.closest('[data-gold-cover][data-gold-cover-variant-id]');
    if (!goldCover) {
      submitter.disabled = false;
      return;
    }

    if (submitter.hasAttribute('data-gold-cover-added')) {
      submitter.disabled = false;
      return;
    }

    const parentUnavailable = goldCover
      .closest('[data-product-section]')
      ?.querySelector('[data-add-to-bag]')
      ?.disabled === true;

    submitter.disabled = goldCover.dataset.goldCoverAvailable !== 'true' || parentUnavailable;
  };

  const hideFeedback = (feedback) => {
    if (!feedback) {
      return;
    }

    const message = feedback.querySelector('[data-cart-error-message]');
    const retry = feedback.querySelector('[data-cart-error-retry]');

    if (message) {
      message.textContent = '';
    }

    if (retry) {
      retry.hidden = true;
      retryActions.delete(retry);
    }

    window.SigmaErrorFeedback?.clear({ message: feedback });
    feedback.hidden = true;
  };

  const clearFeedback = ({ clearStatus = true } = {}) => {
    drawer.querySelectorAll('[data-cart-inline-error], [data-cart-global-error]').forEach(hideFeedback);

    if (clearStatus) {
      const status = getStatus();
      if (status) {
        status.textContent = '';
      }
    }
  };

  const showStatus = (message) => {
    const status = getStatus();

    if (!status) {
      return;
    }

    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 0);
  };

  const friendlyErrorMessage = (error, fallback = 'We couldn’t update your bag. Please try again.') => {
    const message = typeof error === 'string' ? error : error?.message;

    if (!message) {
      return fallback;
    }

    if (/failed to fetch|networkerror|load failed|unexpected token|json/i.test(message)) {
      return 'We couldn’t update your bag. Check your connection and try again.';
    }

    return message.replace(/\bcart\b/gi, (term) => (term === 'Cart' ? 'Bag' : 'bag'));
  };

  const showProductFormError = (form, submitter, message, { recoveryRequired = false } = {}) => {
    let formError = form.querySelector('[data-product-form-error]');

    if (!formError) {
      formError = document.createElement('p');
      formError.dataset.productFormError = '';
      formError.className = 'product-form__error';
      form.append(formError);
    }

    formError.setAttribute('role', 'alert');
    formError.textContent = message;
    formError.hidden = false;
    formError.toggleAttribute('data-cart-recovery-error', recoveryRequired);
    window.SigmaErrorFeedback?.show({ target: submitter, wrapper: form, message: formError });
  };

  const closeSourceDialogBeforeCartDrawer = async (form) => {
    if (!form.hasAttribute('data-close-dialog-on-cart-add')) return;

    const sourceDialog = form.closest('dialog[open]');
    const sourceDialogController = sourceDialog?.closest('landing-brew-guide-transcript');
    if (typeof sourceDialogController?.closeDialog === 'function') {
      await sourceDialogController.closeDialog();
    } else {
      sourceDialog?.close();
    }
  };

  const getBagVariantQuantity = (variantId) =>
    [...drawer.querySelectorAll('[data-cart-line-item]')].reduce((total, lineItem) => {
      if (lineItem.dataset.cartVariantId !== String(variantId)) {
        return total;
      }

      const quantity = Number(lineItem.dataset.cartQuantity);
      return total + (Number.isInteger(quantity) ? quantity : 0);
    }, 0);

  const getAddQuantityError = (form, formData, privateMysteryReceipt = null) => {
    const productRoot = form.closest('[data-product-section]');
    const variantsSource = productRoot?.querySelector('[data-product-variants]');

    if (!variantsSource) {
      return '';
    }

    try {
      const variants = JSON.parse(variantsSource.textContent || '[]');
      const variantId = String(formData.get('id') || '');
      const variant = variants.find((candidate) => String(candidate.id) === variantId);

      if (!variant) {
        return '';
      }

      if (hasValidPrivateMysteryAddReceipt(form, formData, privateMysteryReceipt)) {
        return '';
      }

      const availability = getAddQuantityAvailability({
        inventoryManagement: variant.inventoryManagement,
        inventoryPolicy: variant.inventoryPolicy,
        inventoryQuantity: variant.inventoryQuantity,
        bagQuantity: getBagVariantQuantity(variantId),
        requestedQuantity: Number(formData.get('quantity') || 1),
      });

      if (availability.allowed) {
        return '';
      }

      if (availability.remainingQuantity === 0) {
        return 'All available stock for this item is already in your bag. No items were added.';
      }

      return `Only ${availability.remainingQuantity} more can be added to your bag. No items were added.`;
    } catch {
      return '';
    }
  };

  const isRetryableCartError = (error) =>
    Boolean(error?.retryable) || /failed to fetch|networkerror|load failed|unexpected token|json/i.test(error?.message || '');

  const showError = (
    error,
    { target = null, control = null, retry = null, retryLabel = 'Try again', fallback } = {},
  ) => {
    const feedback = target || getGlobalError();

    if (!feedback) {
      return;
    }

    const message = feedback.querySelector('[data-cart-error-message]');
    const retryButton = feedback.querySelector('[data-cart-error-retry]');

    if (message) {
      message.textContent = friendlyErrorMessage(error, fallback);
    }

    if (retryButton) {
      retryButton.hidden = !retry;
      retryButton.textContent = retryLabel;

      if (retry) {
        retryActions.set(retryButton, retry);
      } else {
        retryActions.delete(retryButton);
      }
    }

    feedback.hidden = false;
    window.SigmaErrorFeedback?.show({
      target: control,
      wrapper: feedback.parentElement,
      message: feedback,
    });
    feedback.scrollIntoView({ block: 'nearest' });
  };

  const setBusy = (busy, trigger = null) => {
    drawer.toggleAttribute('aria-busy', busy);
    drawer.toggleAttribute('data-cart-busy', busy);

    drawer.querySelectorAll('[data-cart-lock-control]').forEach((control) => {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) {
        return;
      }

      if (control === trigger) {
        if (control.matches('.button')) {
          setButtonLoading(control, busy);
          return;
        }

        if (busy) {
          control.setAttribute('aria-busy', 'true');
        } else {
          control.removeAttribute('aria-busy');
        }
      }

      if (busy && !control.disabled) {
        control.disabled = true;
        control.dataset.cartTemporarilyDisabled = 'true';
      } else if (!busy && control.dataset.cartTemporarilyDisabled === 'true') {
        control.disabled = false;
        delete control.dataset.cartTemporarilyDisabled;
      }
    });

    if (!busy) {
      setButtonLoading(trigger, false);
      syncCheckoutPending();
    }
  };

  const captureViewState = (
    trigger,
    { removing = false, restoreFocus = true, rewardRefresh = false } = {},
  ) => {
    const line = trigger?.closest('[data-cart-line-item]');
    const lines = Array.from(drawer.querySelectorAll('[data-cart-line-item]'));
    const lineIndex = line ? lines.indexOf(line) : -1;
    const notePanel = drawer.querySelector('[data-cart-note-panel]');
    const shippingFill = drawer.querySelector('[data-cart-shipping-fill]');
    const content = getDrawerContent();
    const main = content?.querySelector('.cart-drawer__main');

    return {
      action: trigger?.dataset.cartAction || '',
      focusKey: trigger?.dataset.cartFocus || '',
      itemKey: line?.dataset.cartKey || trigger?.dataset.cartKey || '',
      nextItemKey: lineIndex >= 0 ? lines[lineIndex + 1]?.dataset.cartKey || '' : '',
      previousItemKey: lineIndex > 0 ? lines[lineIndex - 1]?.dataset.cartKey || '' : '',
      noteExpanded: Boolean(notePanel && !notePanel.hidden),
      removing,
      restoreFocus,
      rewardRefresh,
      scrollTop: drawer.scrollTop,
      documentScrollTop: drawer.dataset.modalDocumentScroll === 'active' ? window.scrollY : null,
      contentScrollTop: content?.scrollTop || 0,
      mainScrollTop: main?.scrollTop || 0,
      shippingProgress: shippingFill?.style.getPropertyValue('--cart-shipping-progress') || '',
    };
  };

  const findLineFocusTarget = (viewState) => {
    const escape = window.CSS?.escape;

    if (!escape) {
      return null;
    }

    if (viewState.removing) {
      const adjacentKey = viewState.nextItemKey || viewState.previousItemKey;
      return adjacentKey
        ? drawer.querySelector(`[data-cart-line-item][data-cart-key="${escape(adjacentKey)}"] .cart-line__title`)
        : drawer.querySelector('[data-cart-empty-cta]');
    }

    if (viewState.itemKey && viewState.action) {
      const actionTarget = drawer.querySelector(
        `[data-cart-key="${escape(viewState.itemKey)}"][data-cart-action="${escape(viewState.action)}"]`,
      );

      if (!(actionTarget instanceof HTMLButtonElement) || !actionTarget.disabled) {
        return actionTarget;
      }

      return actionTarget.closest('[data-cart-line-item]')?.querySelector('[data-cart-quantity-input]');
    }

    if (viewState.focusKey) {
      return drawer.querySelector(`[data-cart-focus="${escape(viewState.focusKey)}"]`);
    }

    return null;
  };

  const restoreViewState = (viewState, { restoreShippingProgress = true } = {}) => {
    if (!viewState) {
      return;
    }

    const notePanel = drawer.querySelector('[data-cart-note-panel]');
    const noteToggle = drawer.querySelector('[data-cart-note-toggle]');

    if (viewState.noteExpanded && notePanel && noteToggle) {
      notePanel.hidden = false;
      noteToggle.setAttribute('aria-expanded', 'true');
    }

    const shippingFill = drawer.querySelector('[data-cart-shipping-fill]');
    if (restoreShippingProgress && shippingFill && viewState.shippingProgress) {
      const nextProgress = shippingFill.style.getPropertyValue('--cart-shipping-progress');
      shippingFill.style.setProperty('--cart-shipping-progress', viewState.shippingProgress);
      window.requestAnimationFrame(() => {
        shippingFill.style.setProperty('--cart-shipping-progress', nextProgress);
      });
    }

    drawer.scrollTo({ top: viewState.scrollTop, behavior: 'auto' });
    if (drawer.dataset.modalDocumentScroll === 'active' && Number.isFinite(viewState.documentScrollTop)) {
      window.scrollTo({ top: viewState.documentScrollTop, behavior: 'auto' });
    }
    const content = getDrawerContent();
    if (content) content.scrollTop = viewState.contentScrollTop || 0;
    const main = content?.querySelector('.cart-drawer__main');
    if (main) main.scrollTop = viewState.mainScrollTop || 0;

    if (viewState.restoreFocus === false) {
      return;
    }

    const focusTarget = findLineFocusTarget(viewState) || drawer.querySelector('[data-cart-drawer-close]');
    focusTarget?.focus({ preventScroll: true });

    window.requestAnimationFrame(() => {
      focusTarget?.scrollIntoView({ block: 'nearest' });
    });
  };

  const renderSection = async (sectionHtml, viewState = null, renderGuard = {}) => {
    const hasRenderGuard = Boolean(renderGuard.signal || renderGuard.isCurrent);
    const canRender = () => (
      !renderGuard.signal?.aborted
      && (typeof renderGuard.isCurrent !== 'function' || renderGuard.isCurrent())
    );

    if (!canRender()) {
      throw new Error('The bag refresh was cancelled.');
    }

    if (!sectionHtml) {
      throw new Error('The bag could not be refreshed. Please try again.');
    }

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextContent = parsed.querySelector('[data-cart-drawer-content]');
    const currentContent = getDrawerContent();

    if (!nextContent || !currentContent) {
      throw new Error('The bag could not be refreshed. Please try again.');
    }

    const previousShipping = currentContent.querySelector('[data-cart-shipping]');
    syncTierShipping(nextContent, { previousShipping });

    const previousShippingState = previousShipping?.dataset.cartShippingState;
    const previousQuantities = readCartQuantities(currentContent);
    const nextShippingState = nextContent.querySelector('[data-cart-shipping]')?.dataset.cartShippingState;
    const previousShippingCopy = currentContent.querySelector('[data-cart-shipping-copy]');
    const nextShippingCopy = nextContent.querySelector('[data-cart-shipping-copy]');
    const previousShippingText = previousShippingCopy?.textContent.replace(/\s+/g, ' ').trim();
    const nextShippingText = nextShippingCopy?.textContent.replace(/\s+/g, ' ').trim();
    const shouldAnimateShippingCopy = Boolean(
      drawer.open &&
        !reducedMotion.matches &&
        !hasRenderGuard &&
        previousShippingCopy &&
        nextShippingCopy &&
        previousShippingText !== nextShippingText,
    );
    const previousShippingProgress = currentContent
      .querySelector('[data-cart-shipping-fill]')
      ?.style.getPropertyValue('--cart-shipping-progress');
    const nextShippingProgress = nextContent
      .querySelector('[data-cart-shipping-fill]')
      ?.style.getPropertyValue('--cart-shipping-progress');
    const shippingJustUnlocked = previousShippingState !== 'unlocked' && nextShippingState === 'unlocked';
    const currentVoucherSlot = currentContent.querySelector('[data-loyalty-redemption-slot]');
    const nextVoucherSlot = nextContent.querySelector('[data-loyalty-redemption-slot]');
    const reservedVoucherBlockSize = currentVoucherSlot?.style.getPropertyValue('min-block-size');
    const currentLoyaltySummary = currentVoucherSlot?.closest('sigma-loyalty-summary');
    const nextLoyaltySummary = nextVoucherSlot?.closest('sigma-loyalty-summary');
    const currentLoyaltyBlockSize = currentLoyaltySummary?.getBoundingClientRect().height || 0;

    if (viewState?.rewardRefresh === true && nextVoucherSlot && reservedVoucherBlockSize) {
      nextVoucherSlot.style.setProperty('min-block-size', reservedVoucherBlockSize);
    }

    if (viewState?.rewardRefresh === true && nextLoyaltySummary && currentLoyaltyBlockSize > 0) {
      nextLoyaltySummary.dataset.loyaltyReplacementHeight = 'true';
      nextLoyaltySummary.style.setProperty('min-block-size', `${currentLoyaltyBlockSize}px`);
    }

    if (shouldAnimateShippingCopy) {
      previousShippingCopy.classList.add('is-exit');
      await new Promise((resolve) => window.setTimeout(resolve, SHIPPING_TEXT_SWAP_DURATION));
    }

    if (!canRender()) {
      throw new Error('The bag refresh was cancelled.');
    }

    currentContent.replaceWith(nextContent);
    syncCheckoutPending();
    syncHeaderCount();
    initializeCartQuantityTickers(previousQuantities);
    initializeBundleQuantityControls(drawer);
    initializeCartRecommendations(drawer);
    restoreViewState(viewState, { restoreShippingProgress: !shippingJustUnlocked });
    syncGoldCoverButtonsFromDrawer();

    if (shouldAnimateShippingCopy) {
      const updatedShippingCopy = drawer.querySelector('[data-cart-shipping-copy]');

      updatedShippingCopy?.classList.add('is-enter-start');
      updatedShippingCopy?.getBoundingClientRect();
      window.requestAnimationFrame(() => updatedShippingCopy?.classList.remove('is-enter-start'));
    }

    if (shippingJustUnlocked) {
      const updatedShipping = drawer.querySelector('[data-cart-shipping]');

      pendingShippingCelebration = {
        from: previousShippingProgress || '0%',
        to: nextShippingProgress || '100%',
      };

      updatedShipping?.querySelector('[data-cart-shipping-fill]')?.style.setProperty(
        '--cart-shipping-progress',
        pendingShippingCelebration.from,
      );

      if (updatedShipping && !reducedMotion.matches) {
        updatedShipping.dataset.cartShippingTransition = 'unlocking';
        updatedShipping.getBoundingClientRect();
        window.requestAnimationFrame(() => delete updatedShipping.dataset.cartShippingTransition);
      }

      if (drawer.open && drawer.dataset.cartDrawerState !== 'closing') {
        playShippingCelebration();
      }
    }
  };

  const fetchSection = async (requestedSectionId = cartSectionId, route = routesRoot) => {
    const sectionUrl = new URL(route, window.location.origin);
    sectionUrl.searchParams.set('section_id', requestedSectionId);
    const response = await fetch(sectionUrl, {
      cache: 'no-store',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('The bag could not be refreshed. Please try again.');
    }

    return response.text();
  };

  const readAuthoritativeCart = async () => {
    const pageSectionId = getCartPageSectionId();
    const cartResponsePromise = fetch(`${routesRoot}cart.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const sectionsPromise = Promise.all([
      fetchSection(cartSectionId),
      pageSectionId ? fetchSection(pageSectionId, window.location.href) : Promise.resolve(null),
    ]);
    const [cartResponse, [drawerSection, pageSection]] = await Promise.all([
      cartResponsePromise,
      sectionsPromise,
    ]);

    if (!cartResponse.ok) {
      throw new Error('The bag could not be refreshed. Please reload the page.');
    }

    return {
      cart: validateAuthoritativeCartSnapshot(await cartResponse.json()),
      drawerSection,
      pageSection,
      pageSectionId,
    };
  };

  const renderAuthoritativeCart = async (snapshot, viewState = null, guard = {}) => {
    if (guard.isCurrent?.() === false) return;
    await renderSection(snapshot.drawerSection, viewState, guard);

    if (!snapshot.pageSectionId || guard.isCurrent?.() === false) return;

    const pendingRenders = [];
    window.dispatchEvent(new CustomEvent('sigma:cart-drawer-page-section-update', {
      detail: {
        cart: snapshot.cart,
        isCurrent: guard.isCurrent,
        sectionHtml: snapshot.pageSection,
        waitUntil: (render) => pendingRenders.push(Promise.resolve(render)),
      },
    }));
    await Promise.all(pendingRenders);
  };

  const recoverCartSurfaces = (error, viewState = null) => recoverAuthoritativeCart({
    error,
    readCart: readAuthoritativeCart,
    renderCart: (snapshot, guard) => renderAuthoritativeCart(snapshot, viewState, guard),
  });

  const refreshCartPageFromResponse = async (responseData) => {
    const pageSectionId = getCartPageSectionId();

    if (!pageSectionId) {
      return;
    }

    const bundledPageSection = responseData?.sections?.[pageSectionId];
    const pageSectionHtml = typeof bundledPageSection === 'string' && bundledPageSection.trim()
      ? bundledPageSection
      : await fetchSection(pageSectionId, window.location.href);

    const pendingRenders = [];
    window.dispatchEvent(new CustomEvent('sigma:cart-drawer-page-section-update', {
      detail: {
        sectionHtml: pageSectionHtml,
        waitUntil: (render) => pendingRenders.push(Promise.resolve(render)),
      },
    }));
    await Promise.all(pendingRenders);
  };

  const refreshFromResponse = async (responseData, viewState = null) => {
    const sectionHtml = requireCartSectionHtml(responseData, cartSectionId);
    await renderSection(sectionHtml, viewState);
    await refreshCartPageFromResponse(responseData);
  };

  const readJson = readCartMutationJson;

  const fetchFreshCart = async () => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), CART_PREFLIGHT_TIMEOUT);

    try {
      const response = await fetch(`${routesRoot}cart.js`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
        signal: controller.signal,
      });
      const cart = await readJson(response, 'We couldn’t check your bag. Please try again.');

      if (!response.ok) {
        throw new Error(cart.description || cart.message || 'We couldn’t check your bag.');
      }

      return cart;
    } catch (cause) {
      const error = new Error('We couldn’t check your bag. Please try again.', { cause });
      error.retryable = true;
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const syncGoldCoverButtonFromCart = async (button, cart = null) => {
    if (!(button instanceof HTMLButtonElement) || button.form?.hasAttribute('aria-busy')) return;

    const form = button.form;
    if (!(form instanceof HTMLFormElement)) return;

    const selectedVariantId = String(new FormData(form).get('id') || '');
    const freshCart = cart || await fetchFreshCart();

    if (String(new FormData(form).get('id') || '') !== selectedVariantId) return;

    const state = getGoldCoverCartState(button, freshCart, new FormData(form));
    if (state.cover) {
      setGoldCoverAdded(button, true, {
        key: state.cover.key,
        bundleInstance: state.cover.properties?._sigma_bundle_instance,
      });
    } else {
      setGoldCoverAdded(button, false);
    }
  };

  const syncGoldCoverButtonsFromCart = async () => {
    const buttons = [...document.querySelectorAll('[data-gold-cover-add]')];
    if (!buttons.length) return;

    const cart = await fetchFreshCart();
    await Promise.all(buttons.map((button) => syncGoldCoverButtonFromCart(button, cart)));
  };

  const requestCartUpdate = async (endpoint, payload, options = {}) => {
    if (drawer.hasAttribute('data-cart-busy')) {
      return false;
    }

    const {
      errorTarget,
      resolvePayload,
      statusMessage,
      trigger,
      validateResponse,
      viewState,
    } = options;
    const errorTargetId = errorTarget?.id || '';

    if (isCartRecoveryRequired()) {
      showError(new CartRecoveryRequiredError(), {
        target: errorTarget || getGlobalError(),
        control: trigger,
        retry: () => window.location.reload(),
        retryLabel: 'Reload bag',
      });
      return false;
    }

    setBusy(true, trigger);
    clearFeedback();

    const retry = () => requestCartUpdate(endpoint, payload, options);

    return runRecoverableCartMutation(async () => {
      let mutationStarted = false;
      let mutationAccepted = false;

      try {
        const resolvedPayload = resolvePayload ? resolvePayload() : payload;
        const resolvedLine = endpoint === 'cart/change.js' && resolvedPayload?.id
          ? findDrawerLineByKey(String(resolvedPayload.id))
          : null;
        const responseData = await runPrivateMysteryLineMutation(
          resolvedLine || trigger?.closest?.('[data-cart-line-item]'),
          resolvedPayload?.quantity,
          async () => {
            mutationStarted = true;
            const response = await awaitCartMutationResponse(
              fetch(`${routesRoot}${endpoint}`, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                  ...resolvedPayload,
                  sections: getRequestedCartSections(),
                  sections_url: `${window.location.pathname}${window.location.search}`,
                }),
              }),
            );
            const responseCart = await readJson(response, 'We couldn’t update your bag. Please try again.');

            if (!response.ok) {
              const requestError = new Error(
                responseCart.description || responseCart.message || 'We couldn’t update your bag.',
              );
              requestError.retryable = response.status === 429 || response.status >= 500;
              throw requestError;
            }

            mutationAccepted = true;
            return responseCart;
          },
        );
        validateResponse?.(responseData);

        await refreshFromResponse(responseData, viewState);
        showStatus(statusMessage);
        return true;
      } catch (error) {
        let resolvedErrorTarget = errorTarget;
        const uncertainOutcome = mutationAccepted || isCartMutationUncertain(error);
        const requiresAuthoritativeRecovery = mutationStarted
          && (uncertainOutcome || endpoint === 'cart/change.js');
        let presentedError = error;

        if (requiresAuthoritativeRecovery) {
          try {
            await recoverCartSurfaces(error, viewState);
            presentedError = new Error(
              'We couldn’t confirm the update response. Your bag has been refreshed. Review it before checkout.',
            );
            resolvedErrorTarget = errorTargetId ? document.getElementById(errorTargetId) : getGlobalError();
          } catch (recoveryError) {
            presentedError = recoveryError;
            resolvedErrorTarget = getGlobalError() || errorTarget;
            if (recoveryError?.recoveryRequired) {
              lockCartMutationRecovery(recoveryError.lateRecovery);
            }
          }
        }

        const resolvedControl = trigger?.isConnected ? trigger : findLineFocusTarget(viewState);

        showError(presentedError, {
          target: resolvedErrorTarget,
          control: resolvedControl,
          retry: presentedError?.recoveryRequired
            ? () => window.location.reload()
            : (!uncertainOutcome && isRetryableCartError(presentedError) ? retry : null),
          retryLabel: presentedError?.recoveryRequired ? 'Reload bag' : 'Try again',
        });
        return false;
      } finally {
        setBusy(false, trigger);
      }
    }, (queueError) => {
      setBusy(false, trigger);
      showError(queueError, {
        target: errorTarget || getGlobalError(),
        control: trigger,
        retry: () => window.location.reload(),
        retryLabel: 'Reload bag',
      });
      return false;
    });
  };

  const requestBundleLineChange = async ({ key, quantity }) => {
    const response = await awaitCartMutationResponse(
      fetch(`${routesRoot}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: getRequestedCartSections(),
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      }),
    );
    const responseData = await readJson(response, 'We couldn’t update the bundled quantity. Please try again.');

    if (!response.ok) {
      const error = new Error(
        responseData.description || responseData.message || 'We couldn’t update the bundled quantity.',
      );
      error.retryable = response.status === 429 || response.status >= 500;
      throw error;
    }

    return responseData;
  };

  const findBundleParentLine = (plan) => [...drawer.querySelectorAll('[data-cart-line-item]')].find((lineItem) => (
    lineItem.dataset.cartBundleRole === 'parent'
    && lineItem.dataset.cartBundleInstance === plan.bundleInstance
    && lineItem.dataset.cartVariantId === plan.parentVariantId
  ));

  const findDrawerLineByKey = (key) => [...drawer.querySelectorAll('[data-cart-line-item]')]
    .find((lineItem) => lineItem.dataset.cartKey === key);

  const requestBundleQuantityUpdate = async (plan, options = {}) => {
    if (drawer.hasAttribute('data-cart-busy') || !plan?.changes?.length) {
      return false;
    }

    const { errorTarget, trigger, viewState } = options;

    if (isCartRecoveryRequired()) {
      showError(new CartRecoveryRequiredError(), {
        target: errorTarget || getGlobalError(),
        control: trigger,
        retry: () => window.location.reload(),
        retryLabel: 'Reload bag',
      });
      return false;
    }

    setBusy(true, trigger);
    clearFeedback();

    return runRecoverableCartMutation(async () => {
      try {
        const currentParentLine = findBundleParentLine(plan);
        const currentPlan = getBundleQuantityPlan(currentParentLine, plan.targetParentQuantity);

        if (!currentPlan) {
          throw new Error('That bundled item changed before it could be updated. Review your bag and try again.');
        }

        if (!currentPlan.changes.length) {
          showStatus(`Bundled quantity is ${plan.targetParentQuantity}.`);
          return true;
        }

        const responseData = await executeBundleQuantityPlan(currentPlan, requestBundleLineChange);
        const updatedParent = responseData?.items?.find((item) => (
          String(item.variant_id) === currentPlan.parentVariantId
          && item.properties?._sigma_bundle_instance === currentPlan.bundleInstance
          && item.properties?._sigma_bundle_role === 'parent'
        ));

        if (updatedParent?.key && viewState) {
          viewState.itemKey = updatedParent.key;
        }

        await refreshFromResponse(responseData, viewState);
        showStatus(`Bundled quantity updated to ${currentPlan.targetParentQuantity}.`);
        return true;
      } catch (caughtError) {
        let error = caughtError;
        let recoverySucceeded = false;

        try {
          await recoverCartSurfaces(caughtError, viewState);
          recoverySucceeded = true;
        } catch (recoveryError) {
          error = recoveryError;
          if (recoveryError?.recoveryRequired) {
            lockCartMutationRecovery(recoveryError.lateRecovery);
          }
        }

        if (recoverySucceeded && isCartMutationUncertain(caughtError)) {
          error = new Error(
            'We couldn’t confirm the bundled update response. Your bag has been refreshed. Review it before checkout.',
          );
        } else if (recoverySucceeded && caughtError?.rollbackError) {
          error = new Error(
            'We couldn’t keep those bundled quantities together. Your bag has been refreshed. Review it before checkout.',
            { cause: caughtError },
          );
        }

        const currentParentLine = findBundleParentLine(plan);
        const resolvedErrorTarget = currentParentLine?.querySelector('[data-cart-inline-error]') || errorTarget;
        const resolvedControl = findLineFocusTarget(viewState)
          || currentParentLine?.querySelector(`[data-cart-action="${viewState?.action || 'input'}"]`)
          || currentParentLine?.querySelector('[data-cart-quantity-input]')
          || trigger;
        let retry = null;

        if (error?.recoveryRequired) {
          retry = () => window.location.reload();
        } else if (!isCartMutationUncertain(caughtError) && isRetryableCartError(caughtError)) {
          retry = async () => {
            const retryParentLine = findBundleParentLine(plan);
            const retryPlan = getBundleQuantityPlan(retryParentLine, plan.targetParentQuantity);

            if (!retryPlan?.changes?.length) {
              clearFeedback();
              showStatus(`Bundled quantity is ${plan.targetParentQuantity}.`);
              return true;
            }

            return requestBundleQuantityUpdate(retryPlan, {
              errorTarget: retryParentLine.querySelector('[data-cart-inline-error]'),
              trigger: retryParentLine.querySelector(`[data-cart-action="${viewState?.action || 'input'}"]`),
              viewState: captureViewState(
                retryParentLine.querySelector(`[data-cart-action="${viewState?.action || 'input'}"]`),
              ),
            });
          };
        }

        showError(error, {
          target: resolvedErrorTarget,
          control: resolvedControl,
          retry,
          retryLabel: error?.recoveryRequired ? 'Reload bag' : 'Try again',
        });
        return false;
      } finally {
        setBusy(false, trigger);
      }
    }, (queueError) => {
      setBusy(false, trigger);
      showError(queueError, {
        target: errorTarget || getGlobalError(),
        control: trigger,
        retry: () => window.location.reload(),
        retryLabel: 'Reload bag',
      });
      return false;
    });
  };

  const commitQuantityInput = async (quantityInput, { restoreFocus = quantityInput?.matches(':focus') } = {}) => {
    if (
      !(quantityInput instanceof HTMLInputElement)
      || quantityInput.readOnly
      || drawer.hasAttribute('data-cart-busy')
    ) {
      return;
    }

    const line = Number(quantityInput.dataset.cartLine);
    const quantity = Number(quantityInput.value);
    const currentQuantity = Number(quantityInput.defaultValue);
    const errorTarget = quantityInput.closest('[data-cart-line-item]')?.querySelector('[data-cart-inline-error]');

    if (!Number.isInteger(line) || !Number.isInteger(quantity) || quantity < 1) {
      quantityInput.setAttribute('aria-invalid', 'true');
      showError('Enter a whole-number quantity of 1 or more.', { target: errorTarget, control: quantityInput });
      quantityInput.focus();
      return;
    }

    if (quantity === currentQuantity) {
      return;
    }

    prepareShippingCelebrationAudio();

    const viewState = captureViewState(quantityInput);
    viewState.restoreFocus = restoreFocus;
    const itemKey = viewState.itemKey;

    try {
      const bundlePlan = getBundleQuantityPlan(
        quantityInput.closest('[data-cart-line-item]'),
        quantity,
      );

      if (bundlePlan?.changes?.length) {
        await requestBundleQuantityUpdate(bundlePlan, {
          errorTarget,
          trigger: quantityInput,
          viewState,
        });
        return;
      }
    } catch (error) {
      showError(error, { target: errorTarget, control: quantityInput });
      return;
    }

    await requestCartUpdate(
      'cart/change.js',
      { line, quantity },
      {
        errorTarget,
        resolvePayload: () => {
          const currentLine = findDrawerLineByKey(itemKey);
          if (!currentLine) {
            throw new Error('That bag item changed before it could be updated. Review your bag and try again.');
          }
          return { id: currentLine.dataset.cartKey, quantity };
        },
        statusMessage: `Quantity updated to ${quantity}.`,
        trigger: quantityInput,
        viewState,
      },
    );
  };

  const getCurrentDrawerFrame = () => {
    const style = window.getComputedStyle(drawer);
    return {
      opacity: style.opacity,
      transform: style.transform === 'none' ? 'translate3d(0, 0, 0)' : style.transform,
    };
  };

  const runDrawerMotion = (opening) => {
    if (reducedMotion.matches || typeof drawer.animate !== 'function') {
      return null;
    }

    const interruptedFrame = drawerAnimation ? getCurrentDrawerFrame() : null;
    drawerAnimation?.cancel();

    const from =
      interruptedFrame ||
      (opening
        ? { opacity: 0, transform: `translate3d(${DRAWER_MOTION.enterOffset}, 0, 0)` }
        : { opacity: 1, transform: 'translate3d(0, 0, 0)' });
    const to = opening
      ? { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      : { opacity: 0, transform: `translate3d(${DRAWER_MOTION.exitOffset}, 0, 0)` };

    drawerAnimation = drawer.animate([from, to], {
      duration: opening ? DRAWER_MOTION.enterDuration : DRAWER_MOTION.exitDuration,
      easing: opening ? DRAWER_MOTION.enterEasing : DRAWER_MOTION.exitEasing,
      fill: 'both',
    });

    return drawerAnimation;
  };

  const openDrawer = (trigger, { clearExistingFeedback = true } = {}) => {
    window.clearTimeout(closeTimer);
    const wasClosing = drawer.dataset.cartDrawerState === 'closing';
    const wasOpen = drawer.open;

    if (clearExistingFeedback) {
      clearFeedback();
    }

    if (!wasOpen) {
      activeTrigger = trigger || document.activeElement;
      pageScrollX = window.scrollX;
      pageScrollY = window.scrollY;
      drawer.showModal();
    } else if (trigger) {
      activeTrigger = trigger;
    }

    delete drawer.dataset.cartDrawerState;
    setExpanded(true);
    drawer.querySelector('[data-cart-drawer-close]')?.focus({ preventScroll: true });

    if (!wasOpen || wasClosing) {
      const animation = runDrawerMotion(true);
      animation?.finished
        .then(() => {
          if (drawerAnimation === animation) {
            animation.cancel();
            drawerAnimation = null;
            playShippingCelebration();
          }
        })
        .catch(() => {});

      if (!animation) {
        playShippingCelebration();
      }
    }
  };

  const finishClose = (animation = null) => {
    window.clearTimeout(closeTimer);

    if (animation && drawerAnimation !== animation) {
      return;
    }

    window.clearTimeout(confettiTimer);
    window.clearTimeout(confettiCleanupTimer);
    drawer.querySelector('[data-cart-confetti]')?.remove();

    animation?.cancel();
    drawerAnimation = null;
    drawer.close();
    delete drawer.dataset.cartDrawerState;
    setExpanded(false);
    setClearConfirmation(false);
    clearFeedback();
    const returnTargets = [activeTrigger, ...document.querySelectorAll('[data-cart-drawer-trigger]')];
    const restoreReturnFocus = () => {
      const returnTarget = returnTargets.find((target) => target instanceof HTMLElement
        && target.isConnected
        && !target.matches(':disabled')
        && target.getClientRects().length > 0
        && window.getComputedStyle(target).visibility !== 'hidden');
      returnTarget?.focus({ preventScroll: true });
    };
    if (drawer.dataset.modalDocumentScroll === 'active') {
      window.requestAnimationFrame(restoreReturnFocus);
    } else {
      restoreReturnFocus();
    }
    window.scrollTo({ left: pageScrollX, top: pageScrollY, behavior: 'instant' });
    activeTrigger = null;
  };

  const closeDrawer = () => {
    if (!drawer.open || drawer.dataset.cartDrawerState === 'closing') {
      return;
    }

    drawer.dataset.cartDrawerState = 'closing';

    if (reducedMotion.matches) {
      finishClose();
      return;
    }

    const animation = runDrawerMotion(false);

    if (!animation) {
      closeTimer = window.setTimeout(() => finishClose(), DRAWER_MOTION.exitDuration);
      return;
    }

    animation.finished.then(() => finishClose(animation)).catch(() => {});
    closeTimer = window.setTimeout(() => finishClose(animation), DRAWER_MOTION.exitDuration + 80);
  };

  document.addEventListener('click', async (event) => {
    const drawerTrigger = event.target.closest('[data-cart-drawer-trigger]');

    if (drawerTrigger) {
      event.preventDefault();
      prepareShippingCelebrationAudio();
      openDrawer(drawerTrigger);
      return;
    }

    if (!drawer.contains(event.target)) {
      return;
    }

    const retryButton = event.target.closest('[data-cart-error-retry]');
    if (retryButton) {
      const retry = retryActions.get(retryButton);
      clearFeedback();
      await retry?.();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      closeDrawer();
      return;
    }

    if (event.defaultPrevented || drawer.hasAttribute('data-cart-busy')) {
      return;
    }

    const clearCancel = event.target.closest('[data-cart-clear-cancel]');

    if (clearCancel) {
      setClearConfirmation(false, { restoreFocus: true });
      return;
    }

    const clearButton = event.target.closest('[data-cart-clear]');

    if (clearButton) {
      if (!clearButton.hasAttribute('data-cart-clear-confirmed')) {
        setClearConfirmation(true);
        return;
      }

      const clearViewState = captureViewState(clearButton, { removing: true });
      clearViewState.scrollTop = 0;
      clearViewState.documentScrollTop = 0;

      await requestCartUpdate(
        'cart/clear.js',
        {},
        {
          errorTarget: getGlobalError(),
          statusMessage: 'Your bag is now empty.',
          trigger: clearButton,
          validateResponse: (responseData) => {
            if (Number(responseData.item_count) !== 0) {
              throw new Error('We couldn’t empty your bag. Please try again.');
            }
          },
          viewState: clearViewState,
        },
      );
      return;
    }

    const noteToggle = event.target.closest('[data-cart-note-toggle]');

    if (noteToggle) {
      const notePanel = drawer.querySelector('[data-cart-note-panel]');
      const expanded = noteToggle.getAttribute('aria-expanded') === 'true';
      noteToggle.setAttribute('aria-expanded', String(!expanded));

      if (notePanel) {
        notePanel.hidden = expanded;

        if (!expanded) {
          resetNoteDraft();
          notePanel.querySelector('[data-cart-note]')?.focus();
        } else {
          resetNoteDraft();
        }
      }
      return;
    }

    const noteSave = event.target.closest('[data-cart-note-save]');

    const noteDismiss = event.target.closest('[data-cart-note-dismiss]');

    if (noteDismiss) {
      const notePanel = drawer.querySelector('[data-cart-note-panel]');
      const noteToggle = drawer.querySelector('[data-cart-note-toggle]');

      if (notePanel) {
        notePanel.hidden = true;
      }

      noteToggle?.setAttribute('aria-expanded', 'false');
      resetNoteDraft();
      noteToggle?.focus({ preventScroll: true });
      return;
    }

    const noteRemove = event.target.closest('[data-cart-note-remove]');

    if (noteRemove) {
      const viewState = captureViewState(noteRemove);
      viewState.noteExpanded = false;
      viewState.focusKey = 'note-toggle';

      await requestCartUpdate(
        'cart/update.js',
        { note: '' },
        {
          errorTarget: drawer.querySelector('#CartNoteError'),
          statusMessage: 'Order note removed.',
          trigger: noteRemove,
          validateResponse: (responseData) => {
            if (typeof responseData.note === 'string' && responseData.note.trim().length > 0) {
              throw new Error('We couldn’t remove your order note. Please try again.');
            }
          },
          viewState,
        },
      );
      return;
    }

    if (noteSave) {
      const note = drawer.querySelector('[data-cart-note]');
      const errorTarget = drawer.querySelector('#CartNoteError');

      if (!note) {
        return;
      }

      const noteValue = note.value;
      const hasNote = noteValue.trim().length > 0;

      if (!hasNote) {
        note.setAttribute('aria-invalid', 'true');
        showError('Enter an order note before saving.', { target: errorTarget, control: note });
        note.focus();
        return;
      }

      const viewState = captureViewState(noteSave);
      viewState.noteExpanded = false;
      viewState.focusKey = 'note-toggle';

      await requestCartUpdate('cart/update.js', { note: noteValue }, {
        errorTarget,
        statusMessage: 'Order note saved.',
        trigger: noteSave,
        viewState,
      });
      return;
    }

    const cartChange = event.target.closest('[data-cart-change]');

    if (cartChange) {
      const line = Number(cartChange.dataset.cartLine);
      const quantity = Number(cartChange.dataset.cartQuantity);

      if (!Number.isInteger(line) || !Number.isInteger(quantity)) {
        return;
      }

      prepareShippingCelebrationAudio();

      if (quantity > 0) {
        try {
          const bundlePlan = getBundleQuantityPlan(
            cartChange.closest('[data-cart-line-item]'),
            quantity,
          );

          if (bundlePlan?.changes?.length) {
            await requestBundleQuantityUpdate(bundlePlan, {
              errorTarget: cartChange.closest('[data-cart-line-item]')?.querySelector('[data-cart-inline-error]'),
              trigger: cartChange,
              viewState: captureViewState(cartChange),
            });
            return;
          }
        } catch (error) {
          showError(error, {
            target: cartChange.closest('[data-cart-line-item]')?.querySelector('[data-cart-inline-error]'),
            control: cartChange,
          });
          return;
        }
      }

      await requestCartUpdate(
        'cart/change.js',
        { line, quantity },
        {
          errorTarget: cartChange.closest('[data-cart-line-item]')?.querySelector('[data-cart-inline-error]'),
          statusMessage: quantity === 0 ? 'Item removed from your bag.' : `Quantity updated to ${quantity}.`,
          trigger: cartChange,
          viewState: captureViewState(cartChange, { removing: quantity === 0 }),
          resolvePayload: () => {
            const currentLine = findDrawerLineByKey(cartChange.dataset.cartKey || '');
            if (!currentLine) {
              throw new Error('That bag item changed before it could be updated. Review your bag and try again.');
            }
            return { id: currentLine.dataset.cartKey, quantity };
          },
        },
      );
    }
  });

  const attachNativeFormTrackingSuppressors = () => {
    document.querySelectorAll('form[action]').forEach((form) => {
      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const action = new URL(form.action, window.location.href);

      if (!action.pathname.endsWith('/cart/add')) {
        return;
      }

      form.addEventListener('submit', (event) => {
        if (event.defaultPrevented) {
          return;
        }

        // Gold Cover uses a Request object to avoid a second STOQ prompt.
        // Shopify does not track that fetch path, so retain its form event.
        if (event.submitter?.matches('[data-gold-cover-add]')) {
          return;
        }

        nativeFormTrackingSuppressedEvents.add(event);
        event.preventDefault();
      });
    });
  };

  // Product modules register their validators before DOMContentLoaded. Attach
  // before Shopify's load listener so only a successful AJAX add is tracked.
  document.addEventListener('DOMContentLoaded', attachNativeFormTrackingSuppressors, {
    once: true,
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (event.defaultPrevented && !nativeFormTrackingSuppressedEvents.has(event)) {
      return;
    }

    const action = new URL(form.action, window.location.href);

    if (!action.pathname.endsWith('/cart/add')) {
      return;
    }

    if (form.hasAttribute('aria-busy')) {
      event.preventDefault();
      return;
    }

    prepareShippingCelebrationAudio();
    event.preventDefault();
    const submitter = event.submitter;
    const removeGoldCover = Boolean(
      submitter?.matches('[data-gold-cover-add][data-gold-cover-added]'),
    );
    const formData = new FormData(form);
    const bundleChoiceNames = new Set(
      [...form.elements]
        .filter((element) => element.matches?.('[data-bundle-choice-control][name]'))
        .map((element) => element.name)
        .filter(Boolean),
    );
    bundleChoiceNames.forEach((name) => formData.delete(name));
    const existingFormError = form.querySelector('[data-product-form-error]');

    if (existingFormError) {
      window.SigmaErrorFeedback?.clear({ target: submitter, wrapper: form, message: existingFormError });
      existingFormError.textContent = '';
      existingFormError.hidden = true;
    }

    if (isCartRecoveryRequired()) {
      showProductFormError(
        form,
        submitter,
        new CartRecoveryRequiredError().message,
        { recoveryRequired: true },
      );
      return;
    }

    formData.delete('form_type');
    formData.delete('utf8');
    formData.delete('product-id');
    formData.delete('section-id');
    form.toggleAttribute('aria-busy', true);
    setButtonLoading(submitter, true);

    if (submitter instanceof HTMLButtonElement && !submitter.matches('.button')) {
      submitter.disabled = true;
    }

    let bundleRequest = null;

    try {
      await runPrivateMysteryAddMutation(form, () => runRecoverableCartMutation(async () => {
        if (!form.isConnected) {
          showError(
            'Your bag changed before this item could be added. Review your bag and try again.',
            { target: getGlobalError() },
          );
          return;
        }

        if (removeGoldCover) {
          let removalStarted = false;
          let removalConfirmed = false;

          try {
            const cart = await fetchFreshCart();

            const target = resolveGoldCoverCartItem(submitter, cart);
            if (!target) {
              setButtonLoading(submitter, false);
              setGoldCoverAdded(submitter, false);
              showStatus('Gold Cover is no longer in your bag.');
              return;
            }

            setGoldCoverAdded(submitter, true, {
              key: target.key,
              bundleInstance: target.properties?._sigma_bundle_instance,
            });

            const requestedSections = getRequestedCartSections();
            const requestBody = JSON.stringify({
              id: target.key,
              quantity: 0,
              sections: requestedSections,
              sections_url: `${window.location.pathname}${window.location.search}`,
            });
            removalStarted = true;
            const response = await awaitCartMutationResponse(
              fetch(`${routesRoot}cart/change.js`, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: requestBody,
              }),
            );
            const responseData = await readJson(
              response,
              'We couldn’t remove Gold Cover. Please try again.',
            );

            if (!response.ok) {
              throw new Error(
                responseData.description
                || responseData.message
                || 'We couldn’t remove Gold Cover.',
              );
            }

            removalConfirmed = true;
            await refreshFromResponse(responseData);
            setButtonLoading(submitter, false);
            setGoldCoverAdded(submitter, false);
            showStatus('Gold Cover removed from your bag.');
          } catch (error) {
            const requiresAuthoritativeRecovery = removalConfirmed
              || (removalStarted && isCartMutationUncertain(error));
            let presentedError = error;

            if (requiresAuthoritativeRecovery) {
              try {
                await recoverCartSurfaces(error);
                openDrawer(submitter || form, { clearExistingFeedback: false });
                presentedError = new Error(
                  'We couldn’t confirm the removal response. Your bag has been refreshed. Review it before trying again.',
                );
              } catch (recoveryError) {
                presentedError = recoveryError;
                if (recoveryError?.recoveryRequired) {
                  lockCartMutationRecovery(recoveryError.lateRecovery);
                }
              }
            }

            if (removalConfirmed) {
              setButtonLoading(submitter, false);
              setGoldCoverAdded(submitter, false);
            }
            showProductFormError(
              form,
              submitter,
              friendlyErrorMessage(presentedError, 'We couldn’t remove Gold Cover. Please try again.'),
              { recoveryRequired: Boolean(presentedError?.recoveryRequired) },
            );
          }
          return;
        }

        let mutationStarted = false;
        let mutationAccepted = false;

        try {
          const privateMysteryReceipt = await preparePrivateMysteryAdd(form, formData);
          const requestedSections = getRequestedCartSections();
          formData.set('sections', requestedSections.join(','));
          formData.set('sections_url', `${window.location.pathname}${window.location.search}`);
          bundleRequest = buildBundleCartAddRequest(form, formData, {
            sections: requestedSections,
            sectionsUrl: `${window.location.pathname}${window.location.search}`,
            submitter,
          });
          const isGoldCoverSubmit = Boolean(submitter?.matches('[data-gold-cover-add]'));
          let attachedToExistingParent = false;

          if (isGoldCoverSubmit) {
            const cart = await fetchFreshCart();
            const state = getGoldCoverCartState(submitter, cart, formData);

            if (state.cover) {
              setButtonLoading(submitter, false);
              setGoldCoverAdded(submitter, true, {
                key: state.cover.key,
                bundleInstance: state.cover.properties?._sigma_bundle_instance,
              });
              showStatus('Gold Cover is already in your bag.');
              return;
            }

            if (state.standaloneCover) {
              throw new Error(
                'Gold Cover is already in your bag, but it is not attached to this product. Remove it from your bag before adding this offer.',
              );
            }

            if (state.parent) {
              bundleRequest = buildGoldCoverExistingParentRequest(bundleRequest, state.parent, submitter);
              attachedToExistingParent = true;
            }
          }

          if (!attachedToExistingParent) {
            const addQuantityError = getAddQuantityError(form, formData, privateMysteryReceipt);

            if (addQuantityError) {
              form.removeAttribute('aria-busy');
              setButtonLoading(submitter, false);
              restoreSubmitterDisabledState(submitter);
              showProductFormError(form, submitter, addQuantityError);
              return;
            }
          }

          const requestFallback = bundleRequest
            ? 'We couldn’t add these items together. Please try again.'
            : 'We couldn’t add this item. Please try again.';
          const requestBody = bundleRequest ? JSON.stringify(bundleRequest.payload) : formData;
          const requestUrl = `${routesRoot}cart/add.js`;
          const requestOptions = {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              ...(bundleRequest ? { 'Content-Type': 'application/json' } : {}),
            },
            body: requestBody,
          };
          // Gold Cover already has scoped consent and validated plans. A Request input
          // avoids STOQ opening a second confirmation behind the active theme modal.
          const goldCoverRequest = isGoldCoverSubmit && bundleRequest
            ? new Request(new URL(requestUrl, window.location.origin), requestOptions)
            : null;
          mutationStarted = true;
          const response = await awaitCartMutationResponse(
            goldCoverRequest
              ? fetch(goldCoverRequest)
              : fetch(requestUrl, requestOptions),
          );
          const responseData = await readJson(response, requestFallback);

          if (!response.ok) {
            const stockError = resolveAddToBagStockError({
              status: response.status,
              description: responseData.description,
              message: responseData.message,
            });
            const requestError = new Error(
              bundleRequest && stockError.soldOut
                ? 'One of these items just sold out. Review the offer and try again.'
                : stockError.message || requestFallback,
            );
            requestError.variantSoldOut = stockError.soldOut && !bundleRequest;
            throw requestError;
          }

          mutationAccepted = true;
          const goldCoverTarget = submitter?.matches('[data-gold-cover-add]')
            ? getGoldCoverTargetFromResponse(submitter, responseData)
            : null;
          if (goldCoverTarget) setGoldCoverAdded(submitter, true, goldCoverTarget);
          if (goldCoverTarget) submitter.setAttribute('data-gold-cover-confirming', '');
          await refreshFromResponse(responseData);
          if (goldCoverTarget) {
            setButtonLoading(submitter, false);
            // Let the confirmation finish before the bag takes over the viewport.
            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
              await new Promise((resolve) => window.setTimeout(resolve, 250));
            }
            submitter.removeAttribute('data-gold-cover-confirming');
          }
          await closeSourceDialogBeforeCartDrawer(form);
          openDrawer(submitter || form, { clearExistingFeedback: false });
          const addedParent = responseData.items?.[0] || responseData;
          const addedTitle = addedParent.product_title || addedParent.title || 'Item';
          showStatus(
            attachedToExistingParent
              ? 'Gold Cover added to your bag.'
              : bundleRequest
              ? `${addedTitle} and ${bundleRequest.childCount === 1 ? 'its add-on' : `${bundleRequest.childCount} add-ons`} added to your bag.`
              : `${addedTitle} added to your bag.`,
          );
        } catch (error) {
          const requiresAuthoritativeRecovery = mutationAccepted
            || (mutationStarted && isCartMutationUncertain(error));
          let presentedError = error;

          if (requiresAuthoritativeRecovery) {
            try {
              await recoverCartSurfaces(error);
              await closeSourceDialogBeforeCartDrawer(form);
              openDrawer(submitter || form, { clearExistingFeedback: false });
              presentedError = new Error(
                'We couldn’t confirm the add response. Your bag has been refreshed. Review it before trying again.',
              );
            } catch (recoveryError) {
              presentedError = recoveryError;
              if (recoveryError?.recoveryRequired) {
                lockCartMutationRecovery(recoveryError.lateRecovery);
              }
            }
          }

          submitter?.removeAttribute('data-gold-cover-confirming');
          if (error?.variantSoldOut) {
            form.dispatchEvent(
              new CustomEvent('sigma:product-variant-sold-out', {
                bubbles: true,
                detail: { variantId: String(formData.get('id') || '') },
              }),
            );
          }

          showProductFormError(
            form,
            submitter,
            friendlyErrorMessage(
              presentedError,
              bundleRequest
                ? 'We couldn’t add these items together. Please try again.'
                : 'We couldn’t add this item. Please try again.',
            ),
            { recoveryRequired: Boolean(presentedError?.recoveryRequired) },
          );
        }
      }, (queueError) => {
        showProductFormError(form, submitter, queueError.message, {
          recoveryRequired: Boolean(queueError?.recoveryRequired),
        });
        return false;
      }));
    } catch (error) {
      showProductFormError(
        form,
        submitter,
        friendlyErrorMessage(error, 'We couldn’t reserve this box. Please try again.'),
      );
    } finally {
      form.removeAttribute('aria-busy');
      setButtonLoading(submitter, false);
      restoreSubmitterDisabledState(submitter);
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-option-input], [data-gold-cover-terms]')) return;

    const button = event.target
      .closest('[data-product-section]')
      ?.querySelector('[data-gold-cover-add]');

    if (!(button instanceof HTMLButtonElement) || button.form?.hasAttribute('aria-busy')) return;

    if (event.target.matches('[data-option-input]')) {
      setGoldCoverAdded(button, false);
      void syncGoldCoverButtonFromCart(button).catch(() => {});
      return;
    }

    if (button.hasAttribute('data-gold-cover-added')) button.disabled = false;
  });

  document.addEventListener('sigma:product-ready', (event) => {
    const button = event.target?.querySelector?.('[data-gold-cover-add]');
    if (!(button instanceof HTMLButtonElement)) return;

    void syncGoldCoverButtonFromCart(button).catch(() => {});
  });

  drawer.addEventListener('change', async (event) => {
    const quantityInput = event.target.closest('[data-cart-quantity-input]');

    window.clearTimeout(quantityInputTimers.get(quantityInput));

    await commitQuantityInput(quantityInput, { restoreFocus: quantityInput?.matches(':focus') });
  });

  drawer.addEventListener('keydown', async (event) => {
    if (event.target.matches('[data-cart-quantity-input]')) {
      if (event.key === 'Enter') {
        event.preventDefault();
        window.clearTimeout(quantityInputTimers.get(event.target));
        await commitQuantityInput(event.target);
        return;
      }
    }

    if (
      event.target.matches('[data-cart-note]') &&
      event.key === 'Enter' &&
      (event.metaKey || event.ctrlKey)
    ) {
      event.preventDefault();
      drawer.querySelector('[data-cart-note-save]')?.click();
      return;
    }

    if (event.key === 'Escape') {
      if (event.target.closest('[data-cart-shipping-info-dialog]')) {
        return;
      }
      event.preventDefault();
      closeDrawer();
    }
  });

  drawer.addEventListener('input', (event) => {
    if (event.target.matches('[data-cart-quantity-input]')) {
      event.target.setAttribute('aria-invalid', 'false');
      hideFeedback(event.target.closest('[data-cart-line-item]')?.querySelector('[data-cart-inline-error]'));

      window.clearTimeout(quantityInputTimers.get(event.target));
      const quantity = Number(event.target.value);
      if (Number.isInteger(quantity) && quantity >= 1 && quantity !== Number(event.target.defaultValue)) {
        const timer = window.setTimeout(() => commitQuantityInput(event.target), 450);
        quantityInputTimers.set(event.target, timer);
      }
      return;
    }

    if (!event.target.matches('[data-cart-note]')) {
      return;
    }

    event.target.setAttribute('aria-invalid', 'false');
    hideFeedback(drawer.querySelector('#CartNoteError'));
  });

  drawer.addEventListener('cancel', (event) => {
    if (event.target !== drawer) {
      return;
    }
    event.preventDefault();
    closeDrawer();
  });

  drawer.addEventListener('click', (event) => {
    if (event.target !== drawer) {
      return;
    }

    const bounds = drawer.getBoundingClientRect();
    const outsideDrawer =
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom;

    if (outsideDrawer) {
      closeDrawer();
    }
  });

  drawer.addEventListener('close', () => {
    // A responsive change can reopen the same dialog in another presentation.
    if (drawer.open) return;
    setExpanded(false);
    delete drawer.dataset.cartDrawerState;
  });

  initializeCartQuantityTickers();
  initializeBundleQuantityControls(drawer);
  initializeCartRecommendations(drawer);
  syncTierShipping(drawer);
  syncHeaderCount();
  void syncGoldCoverButtonsFromCart().catch(() => {});

  window.addEventListener('sigma:cart-page-section-update', (event) => {
    const render = renderSection(event.detail?.sectionHtml, null, {
      isCurrent: event.detail?.isCurrent,
    });
    event.detail?.waitUntil?.(render);
    render.catch(() => {});
  });

  window.addEventListener('sigma:issued-voucher-cart-refresh', (event) => {
    const sectionHtml = event.detail?.sections?.[cartSectionId];
    if (typeof sectionHtml !== 'string' || !sectionHtml.trim()) return;

    const viewState = captureViewState(null, { restoreFocus: false, rewardRefresh: true });
    const render = renderSection(sectionHtml, viewState, {
      signal: event.detail?.signal,
      isCurrent: event.detail?.isCurrent,
    });
    event.detail?.acknowledge?.(cartSectionId, render);
    render.catch(() => {});
  });
}
